/**
 * Lens display helpers.
 *
 * `lenses` carries both `name` (Finnish) and `name_en`, but many UI paths
 * used to read only `name` — so an English workshop showed Finnish lens names
 * ("Talous ja rahoitus" in an en instance). Route every lens label through
 * here so there is one place that decides.
 *
 * Guiding questions (`obstacle_question` / `opportunity_question`) are stored
 * in Finnish only in the DB today. For en/sv we map the standard Kuutio cube
 * (and common simulator aliases) to translations; unknown custom lenses fall
 * back to the stored Finnish text rather than inventing copy.
 */

/** Standard cube lens names (fi) → en / sv when name_en / name_sv are empty. */
const STANDARD_LENS_NAMES = {
  Talous: { en: 'Economy', sv: 'Ekonomi' },
  Politiikka: { en: 'Politics', sv: 'Politik' },
  Teknologia: { en: 'Technology', sv: 'Teknologi' },
  Käyttäytyminen: { en: 'Behaviour', sv: 'Beteende' },
  'Uskomukset ja arvot': { en: 'Beliefs and values', sv: 'Föreställningar och värderingar' },
  'Luonto ja ilmasto': { en: 'Nature and climate', sv: 'Natur och klimat' },
};

function standardLensName(fiName, lang) {
  const entry = STANDARD_LENS_NAMES[fiName];
  if (!entry) return '';
  if (lang === 'sv') return entry.sv || entry.en || '';
  if (lang === 'en') return entry.en || '';
  return '';
}

export function lensDisplayName(lens, language = 'fi') {
  if (!lens) return '';
  const code = String(language || 'fi').toLowerCase().slice(0, 2);
  // A report translated beyond fi / en / sv (#1442) shows the English name, not the Finnish.
  const lang = code === 'fi' || code === 'sv' ? code : 'en';
  const fiName = (lens.name || '').trim();
  if (lang === 'en') {
    const en = (lens.name_en || lens.nameEn || '').trim();
    if (en) return en;
    const mapped = standardLensName(fiName, 'en');
    if (mapped) return mapped;
  }
  if (lang === 'sv') {
    const sv = (lens.name_sv || lens.nameSv || '').trim();
    if (sv) return sv;
    const mapped = standardLensName(fiName, 'sv');
    if (mapped) return mapped;
    // Prefer English over Finnish when Swedish is missing — better than silent FI fallback.
    const en = (lens.name_en || lens.nameEn || '').trim();
    if (en) return en;
    const mappedEn = standardLensName(fiName, 'en');
    if (mappedEn) return mappedEn;
  }
  return fiName;
}

/**
 * Resolve a lensImpact / AI key (often the Finnish lens name) to a display label.
 */
export function lensLabelFromKey(key, lenses = [], language = 'fi') {
  if (!key) return '';
  const found = (lenses || []).find((l) => {
    const names = [l.name, l.name_en, l.nameEn, l.name_sv, l.nameSv]
      .filter(Boolean)
      .map((n) => String(n).trim().toLowerCase());
    return names.includes(String(key).trim().toLowerCase());
  });
  if (found) return lensDisplayName(found, language);
  return String(key);
}

/** Standard cube + simulator aliases → localized guiding questions. */
const STANDARD_LENS_QUESTIONS = [
  {
    match: [/talous/i, /finance|econom/i, /ekonomi|finans/i],
    obstacle: {
      fi: 'Mitä taloudellisia esteitä tähän liittyy?',
      en: 'What economic barriers relate to this?',
      sv: 'Vilka ekonomiska hinder hör ihop med det här?',
    },
    opportunity: {
      fi: 'Mitä taloudellisia mahdollisuuksia tämä avaa?',
      en: 'What economic opportunities does this open?',
      sv: 'Vilka ekonomiska möjligheter öppnar det här?',
    },
  },
  {
    match: [/teknolog/i, /innova/i, /technology/i],
    obstacle: {
      fi: 'Mitä teknologisia esteitä on?',
      en: 'What technological barriers are there?',
      sv: 'Vilka teknologiska hinder finns?',
    },
    opportunity: {
      fi: 'Mitä teknologisia mahdollisuuksia on?',
      en: 'What technological opportunities are there?',
      sv: 'Vilka teknologiska möjligheter finns?',
    },
  },
  {
    match: [/politiik/i, /lainsääd|hallinto/i, /legislat|govern/i, /politik/i],
    obstacle: {
      fi: 'Mitä lainsäädännöllisiä tai poliittisia esteitä on?',
      en: 'What legislative or political barriers are there?',
      sv: 'Vilka lagstiftnings- eller politiska hinder finns?',
    },
    opportunity: {
      fi: 'Mitä hallinnollisia tai poliittisia mahdollisuuksia on?',
      en: 'What governance or political opportunities are there?',
      sv: 'Vilka förvaltnings- eller politiska möjligheter finns?',
    },
  },
  {
    match: [/uskomuk|arvot|yhteiskunta|kulttuuri/i, /societ|cultur|belief|value/i, /samhälle|kultur|värder/i],
    obstacle: {
      fi: 'Mitä sosiaalisia, kulttuurisia tai arvopohjaisia esteitä on?',
      en: 'What social, cultural, or value-based barriers are there?',
      sv: 'Vilka sociala, kulturella eller värdebaserade hinder finns?',
    },
    opportunity: {
      fi: 'Mitä yhteiskunnallisia tai kulttuurisia mahdollisuuksia on?',
      en: 'What societal or cultural opportunities are there?',
      sv: 'Vilka samhälleliga eller kulturella möjligheter finns?',
    },
  },
  {
    match: [/luonto|ilmasto|ympäristö|kestävy/i, /environ|climat|sustain/i, /miljö|klimat|hållbar/i],
    obstacle: {
      fi: 'Mitä ympäristöön tai ilmastoon liittyviä esteitä on?',
      en: 'What environment- or climate-related barriers are there?',
      sv: 'Vilka miljö- eller klimatrelaterade hinder finns?',
    },
    opportunity: {
      fi: 'Mitä kestävyyteen tai luontoon liittyviä mahdollisuuksia on?',
      en: 'What sustainability- or nature-related opportunities are there?',
      sv: 'Vilka hållbarhets- eller naturrelaterade möjligheter finns?',
    },
  },
  {
    match: [/käyttäytyminen|osaaminen|koulutus/i, /behavio|skill|educat/i, /beteende|kompetens|utbild/i],
    obstacle: {
      fi: 'Mitä käyttäytymiseen, osaamiseen tai koulutukseen liittyviä esteitä on?',
      en: 'What behaviour-, skills-, or education-related barriers are there?',
      sv: 'Vilka beteende-, kompetens- eller utbildningsrelaterade hinder finns?',
    },
    opportunity: {
      fi: 'Mitä käyttäytymisen, osaamisen tai koulutuksen mahdollisuuksia on?',
      en: 'What opportunities arise from behaviour, skills, or education?',
      sv: 'Vilka möjligheter finns i beteende, kompetens eller utbildning?',
    },
  },
];

function langKey(language) {
  return String(language || 'fi').toLowerCase().slice(0, 2);
}

function pickQuestion(bag, language, fallback) {
  const lang = langKey(language);
  return (bag && (bag[lang] || (lang === 'sv' ? bag.en : null) || bag.fi)) || fallback || '';
}

function matchStandardQuestions(lens) {
  const haystack = [lens?.name, lens?.name_en, lens?.nameEn, lens?.name_sv, lens?.nameSv]
    .filter(Boolean)
    .join(' | ');
  if (!haystack) return null;
  return STANDARD_LENS_QUESTIONS.find((row) => row.match.some((re) => re.test(haystack))) || null;
}

export function lensObstacleQuestion(lens, language = 'fi') {
  if (!lens) return '';
  const lang = langKey(language);
  const storedEn = (lens.obstacle_question_en || lens.obstacleQuestionEn || '').trim();
  const storedSv = (lens.obstacle_question_sv || lens.obstacleQuestionSv || '').trim();
  const storedFi = (lens.obstacle_question || lens.obstacleQuestion || '').trim();
  if (lang === 'en' && storedEn) return storedEn;
  if (lang === 'sv' && storedSv) return storedSv;
  if (lang === 'fi' && storedFi) return storedFi;
  const standard = matchStandardQuestions(lens);
  if (standard && lang !== 'fi') {
    return pickQuestion(standard.obstacle, language, storedFi);
  }
  return storedFi || pickQuestion(standard?.obstacle, language, '');
}

export function lensOpportunityQuestion(lens, language = 'fi') {
  if (!lens) return '';
  const lang = langKey(language);
  const storedEn = (lens.opportunity_question_en || lens.opportunityQuestionEn || '').trim();
  const storedSv = (lens.opportunity_question_sv || lens.opportunityQuestionSv || '').trim();
  const storedFi = (lens.opportunity_question || lens.opportunityQuestion || '').trim();
  if (lang === 'en' && storedEn) return storedEn;
  if (lang === 'sv' && storedSv) return storedSv;
  if (lang === 'fi' && storedFi) return storedFi;
  const standard = matchStandardQuestions(lens);
  if (standard && lang !== 'fi') {
    return pickQuestion(standard.opportunity, language, storedFi);
  }
  return storedFi || pickQuestion(standard?.opportunity, language, '');
}

/**
 * Cube face images, in cube order. Instances seeded with the standard Kuutio
 * faces already store an image_url; ones created by other paths (the simulator)
 * do not, and rendered as bare gradients. Fall back to the shared cube art by
 * position so every lens grid looks like Kuutio rather than coloured boxes.
 */
export const CUBE_FACE_IMAGES = [
  '/assets/kuutio-faces/talous.png',
  '/assets/kuutio-faces/teknologia.png',
  '/assets/kuutio-faces/politiikka.png',
  '/assets/kuutio-faces/uskomukset_arvot.png',
  '/assets/kuutio-faces/luonto_ilmasto.png',
  '/assets/kuutio-faces/kayttaytyminen.png',
];

export function lensImageUrl(lens, index = 0) {
  const stored = (lens?.image_url || lens?.imageUrl || '').trim();
  if (stored) return stored;
  return CUBE_FACE_IMAGES[index % CUBE_FACE_IMAGES.length];
}
